/**
 * The five register books (spec 8.4). Each has a base object (the columns), a data
 * schema with the cross-field checks a form needs, a row schema with the sync columns,
 * and a strict partial `fields` schema for ops. Cross-field checks are not applied to
 * `fields`, which may carry a single column.
 *
 * The server stays lenient (owner, 2026-10-02). These are the schemas the push route
 * validates every op with (`fieldsSchemas`, sync/visibility.ts), and they do NOT require
 * the fields a book requires (`KOLOM_WAJIB_BUKU`): an entry whose required columns are
 * empty is accepted, ticked or not, so an app from before the rule that has not reloaded
 * yet keeps syncing, exactly as dates the form would refuse today still sync. What they do
 * check is the shape of `tidak_tercatat`: a list of that book's required columns that may
 * be empty, each once. The form requires the fields (`skemaIsian`, ../registers.ts).
 */
import { z } from 'zod'
import { isPartialDate, partialDateEarliest, partialDateLatest } from '../dates/partial'
import {
  BENTUK_PERKAWINAN,
  CARA_PERKAWINAN,
  type ColumnDef,
  contract,
  JENIS_BAPTIS,
  JENIS_KELAMIN,
  STATUS_PERKAWINAN_ENTRI,
  type TABEL_REGISTER,
} from '../schema/contract'
import ui from '../strings/ui.id.json' with { type: 'json' }
import {
  catatanPinggirListSchema,
  optionalTextSchema,
  partialDateSchema,
  registerShape,
  requiredTextSchema,
  syncShape,
  uuidSchema,
} from './kolom'

const pesan = ui.validasi

type Tabel = (typeof TABEL_REGISTER)[number]

/**
 * The columns each book requires of an entry, in the book's order (owner, 2026-10-02).
 * Everything else stays optional: the parents' religion, the second godparent, No. KK
 * Katolik, the baptism references, Dispensasi, the date of birth on a death, Catatan.
 */
export const KOLOM_WAJIB_BUKU: Readonly<Record<Tabel, readonly string[]>> = {
  baptis: [
    'nomor',
    'nama',
    'nama_baptis',
    'tempat_lahir',
    'tanggal_lahir',
    'jenis_kelamin',
    'nama_ayah',
    'nama_ibu',
    'wali_1',
    'tempat',
    'tanggal',
    'pelayan',
    'jenis',
  ],
  komuni_pertama: ['nomor', 'nama', 'tanggal_lahir', 'tanggal', 'tempat', 'pelayan'],
  krisma: [
    'nomor',
    'nama',
    'nama_krisma',
    'tanggal_lahir',
    'wali_krisma',
    'tanggal',
    'tempat',
    'pelayan',
  ],
  perkawinan: [
    'nomor',
    'pria_nama',
    'pria_tanggal_lahir',
    'pria_tempat_lahir',
    'pria_agama',
    'pria_nama_ayah',
    'pria_nama_ibu',
    'wanita_nama',
    'wanita_tanggal_lahir',
    'wanita_tempat_lahir',
    'wanita_agama',
    'wanita_nama_ayah',
    'wanita_nama_ibu',
    'tanggal',
    'tempat',
    'pelayan',
    'saksi_1',
    'saksi_2',
    'bentuk',
    'cara',
    'status',
  ],
  kematian: [
    'nomor',
    'nama',
    'tanggal_meninggal',
    'tempat_meninggal',
    'tanggal_pemakaman',
    'tempat_pemakaman',
    'pelayan',
  ],
}

/**
 * The required columns Mode Salin Buku may tick "Tidak tercatat di buku": those the
 * database lets stay empty. The number, the names and a marriage's status are never empty
 * (NOT NULL in both databases, required before this rule; the status starts at "Sah"), so
 * they carry no tick.
 */
export const KOLOM_TIDAK_TERCATAT: Readonly<Record<Tabel, readonly [string, ...string[]]>> = {
  baptis: bolehKosong('baptis'),
  komuni_pertama: bolehKosong('komuni_pertama'),
  krisma: bolehKosong('krisma'),
  perkawinan: bolehKosong('perkawinan'),
  kematian: bolehKosong('kematian'),
}

function bolehKosong(tabel: Tabel): [string, ...string[]] {
  const kolom: Readonly<Record<string, ColumnDef>> = contract[tabel].columns
  const out = KOLOM_WAJIB_BUKU[tabel].filter((k) => kolom[k]?.nullable === true)
  const [pertama, ...lain] = out
  if (pertama === undefined) throw new Error(`${tabel}: no required column may be empty`)
  return [pertama, ...lain]
}

/** `tidak_tercatat`: the book's columns that may be ticked, each once; null when none is. */
function tidakTercatatSchema(tabel: Tabel) {
  return z
    .array(z.enum(KOLOM_TIDAK_TERCATAT[tabel]))
    .refine((daftar) => new Set(daftar).size === daftar.length, 'tidak_tercatat')
    .nullish()
}

const registerTail = {
  catatan: optionalTextSchema,
  catatan_pinggir: catatanPinggirListSchema.default([]),
}

/**
 * True when every reading of `sakramen` lies before every reading of `lahir`. A value that is
 * not a date (a form's text its own field refused) compares as nothing: its field names it.
 */
export function pastiSebelum(sakramen: string, lahir: string): boolean {
  if (!isPartialDate(sakramen) || !isPartialDate(lahir)) return false
  return partialDateLatest(sakramen) < partialDateEarliest(lahir)
}

/** The book's date after the birth; the sentence names the book's own date (baptism, death…). */
function tanggalSetelahLahir(
  ctx: z.RefinementCtx,
  tanggal: string | null | undefined,
  lahir: string | null | undefined,
  kalimat: string,
) {
  if (tanggal && lahir && pastiSebelum(tanggal, lahir)) {
    ctx.addIssue({ code: 'custom', path: ['tanggal'], message: kalimat })
  }
}

// --- baptis -----------------------------------------------------------------

const baptisBase = z.object({
  ...registerShape,
  umat_id: uuidSchema.nullish(),
  nama: requiredTextSchema,
  nama_baptis: optionalTextSchema,
  jenis_kelamin: z.enum(JENIS_KELAMIN).nullish(),
  tempat_lahir: optionalTextSchema,
  tanggal_lahir: partialDateSchema.nullish(),
  nama_ayah: optionalTextSchema,
  nama_ibu: optionalTextSchema,
  agama_ayah: optionalTextSchema,
  agama_ibu: optionalTextSchema,
  wali_1: optionalTextSchema,
  wali_2: optionalTextSchema,
  jenis: z.enum(JENIS_BAPTIS).nullish(),
  nomor_kk_katolik: optionalTextSchema,
  /** The later sacraments typed on the entry (schema 13, ../sakramenLanjut). */
  komuni_tanggal: partialDateSchema.nullish(),
  komuni_paroki: optionalTextSchema,
  krisma_tanggal: partialDateSchema.nullish(),
  krisma_paroki: optionalTextSchema,
  perkawinan_tanggal: partialDateSchema.nullish(),
  perkawinan_paroki: optionalTextSchema,
  perkawinan_pasangan: optionalTextSchema,
  ...registerTail,
  tidak_tercatat: tidakTercatatSchema('baptis'),
})
export const baptisSchema = baptisBase.superRefine((v, ctx) =>
  tanggalSetelahLahir(ctx, v.tanggal, v.tanggal_lahir, pesan.baptisSebelumLahir),
)
export type Baptis = z.infer<typeof baptisSchema>
export const baptisRowSchema = baptisBase
  .extend(syncShape)
  .superRefine((v, ctx) =>
    tanggalSetelahLahir(ctx, v.tanggal, v.tanggal_lahir, pesan.baptisSebelumLahir),
  )
export type BaptisRow = z.infer<typeof baptisRowSchema>
export const baptisFieldsSchema = z.strictObject(
  baptisBase.extend(syncShape).omit({ _hlc: true }).partial().shape,
)

// --- komuni_pertama ---------------------------------------------------------

const komuniBase = z.object({
  ...registerShape,
  umat_id: uuidSchema.nullish(),
  nama: requiredTextSchema,
  tanggal_lahir: partialDateSchema.nullish(),
  referensi_baptis: optionalTextSchema,
  ...registerTail,
  tidak_tercatat: tidakTercatatSchema('komuni_pertama'),
})
export const komuniPertamaSchema = komuniBase.superRefine((v, ctx) =>
  tanggalSetelahLahir(ctx, v.tanggal, v.tanggal_lahir, pesan.komuniSebelumLahir),
)
export type KomuniPertama = z.infer<typeof komuniPertamaSchema>
export const komuniPertamaRowSchema = komuniBase
  .extend(syncShape)
  .superRefine((v, ctx) =>
    tanggalSetelahLahir(ctx, v.tanggal, v.tanggal_lahir, pesan.komuniSebelumLahir),
  )
export const komuniPertamaFieldsSchema = z.strictObject(
  komuniBase.extend(syncShape).omit({ _hlc: true }).partial().shape,
)

// --- krisma -----------------------------------------------------------------

const krismaBase = z.object({
  ...registerShape,
  umat_id: uuidSchema.nullish(),
  nama: requiredTextSchema,
  nama_krisma: optionalTextSchema,
  tanggal_lahir: partialDateSchema.nullish(),
  wali_krisma: optionalTextSchema,
  referensi_baptis: optionalTextSchema,
  ...registerTail,
  tidak_tercatat: tidakTercatatSchema('krisma'),
})
export const krismaSchema = krismaBase.superRefine((v, ctx) =>
  tanggalSetelahLahir(ctx, v.tanggal, v.tanggal_lahir, pesan.krismaSebelumLahir),
)
export type Krisma = z.infer<typeof krismaSchema>
export const krismaRowSchema = krismaBase
  .extend(syncShape)
  .superRefine((v, ctx) =>
    tanggalSetelahLahir(ctx, v.tanggal, v.tanggal_lahir, pesan.krismaSebelumLahir),
  )
export const krismaFieldsSchema = z.strictObject(
  krismaBase.extend(syncShape).omit({ _hlc: true }).partial().shape,
)

// --- perkawinan -------------------------------------------------------------

const perkawinanBase = z.object({
  ...registerShape,
  pria_nama: requiredTextSchema,
  pria_tanggal_lahir: partialDateSchema.nullish(),
  pria_tempat_lahir: optionalTextSchema,
  pria_agama: optionalTextSchema,
  pria_nama_ayah: optionalTextSchema,
  pria_nama_ibu: optionalTextSchema,
  pria_referensi_baptis: optionalTextSchema,
  pria_umat_id: uuidSchema.nullish(),
  wanita_nama: requiredTextSchema,
  wanita_tanggal_lahir: partialDateSchema.nullish(),
  wanita_tempat_lahir: optionalTextSchema,
  wanita_agama: optionalTextSchema,
  wanita_nama_ayah: optionalTextSchema,
  wanita_nama_ibu: optionalTextSchema,
  wanita_referensi_baptis: optionalTextSchema,
  wanita_umat_id: uuidSchema.nullish(),
  saksi_1: optionalTextSchema,
  saksi_2: optionalTextSchema,
  bentuk: z.enum(BENTUK_PERKAWINAN).nullish(),
  cara: z.enum(CARA_PERKAWINAN).nullish(),
  dispensasi: optionalTextSchema,
  status: z.enum(STATUS_PERKAWINAN_ENTRI).default('sah'),
  ...registerTail,
  tidak_tercatat: tidakTercatatSchema('perkawinan'),
})
export const perkawinanSchema = perkawinanBase
export type Perkawinan = z.infer<typeof perkawinanSchema>
export const perkawinanRowSchema = perkawinanBase.extend(syncShape)
export const perkawinanFieldsSchema = z.strictObject(
  perkawinanRowSchema.omit({ _hlc: true }).partial().shape,
)

// --- kematian ---------------------------------------------------------------

const kematianBase = z.object({
  ...registerShape,
  umat_id: uuidSchema.nullish(),
  nama: requiredTextSchema,
  tanggal_lahir: partialDateSchema.nullish(),
  tanggal_meninggal: partialDateSchema.nullish(),
  tempat_meninggal: optionalTextSchema,
  tanggal_pemakaman: partialDateSchema.nullish(),
  tempat_pemakaman: optionalTextSchema,
  referensi_baptis: optionalTextSchema,
  ...registerTail,
  tidak_tercatat: tidakTercatatSchema('kematian'),
})
export const kematianSchema = kematianBase.superRefine((v, ctx) =>
  tanggalSetelahLahir(ctx, v.tanggal_meninggal, v.tanggal_lahir, pesan.meninggalSebelumLahir),
)
export type Kematian = z.infer<typeof kematianSchema>
export const kematianRowSchema = kematianBase
  .extend(syncShape)
  .superRefine((v, ctx) =>
    tanggalSetelahLahir(ctx, v.tanggal_meninggal, v.tanggal_lahir, pesan.meninggalSebelumLahir),
  )
export const kematianFieldsSchema = z.strictObject(
  kematianBase.extend(syncShape).omit({ _hlc: true }).partial().shape,
)
